import { Fragment } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowDown,
  ArrowUpRight,
  FileCheck,
  Eye,
  Camera,
  PawPrint,
  MessagesSquare,
  Sprout,
  Search,
  HandHeart,
  Boxes,
  Target,
} from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, LineReveal } from "../components/Reveal";

const metrics = [
  {
    label: "Steps walked",
    value: "1,20,827",
    sub: "Original goal: 1,00,000 · 121% of goal",
    live: true,
  },
  { label: "Animals supported", value: null },
  { label: "Meals sponsored", value: null },
  { label: "Veterinary interventions", value: null },
  { label: "Community participants", value: null },
  { label: "Volunteer hours", value: null },
  { label: "Trees planted", value: null },
  { label: "Funds directed to causes", value: null },
];

const reportFormats = [
  {
    title: "Community Walk",
    steps: ["participants", "combined steps", "amount directed to a cause"],
  },
  {
    title: "Meal Sponsorship",
    steps: ["partner organisation", "meals sponsored", "date & place documented"],
  },
];

// A. Three directions of impact — official pillar colours
const impactPillars = [
  {
    name: "Animals",
    icon: PawPrint,
    color: "#43D2FF",
    text: "#0B7A8C",
    on: "#1C1917",
    stand: "Street-animal welfare — feeding, clean water, sterilisation, vaccination and veterinary support, strengthening the people already doing the work.",
    status: "Active initiative — first verified outcome recorded above",
    active: true,
  },
  {
    name: "People",
    icon: MessagesSquare,
    color: "#FF2F89",
    text: "#D6106A",
    on: "#F7F5F0",
    stand: "Mental-health awareness, community conversations and reducing the stigma of asking for support.",
    status: "In development — nothing reported until it's real",
    active: false,
  },
  {
    name: "Nature",
    icon: Sprout,
    color: "#FFC300",
    text: "#8F6D00",
    on: "#1C1917",
    stand: "Community clean-ups, tree planting and practical local environmental action — participation over performance.",
    status: "In development — nothing reported until it's real",
    active: false,
  },
];

// B. The PilCoo reporting model
const reportFlow = [
  { icon: Search, label: "Need", note: "Verified with the community" },
  { icon: HandHeart, label: "Action", note: "The work that responds" },
  { icon: Boxes, label: "Resources", note: "What it took" },
  { icon: Target, label: "Outcome", note: "What changed" },
  { icon: FileCheck, label: "Evidence", note: "Counts, photos, receipts" },
];

// C. What accountability will document
const accountabilityItems = [
  "What was supported",
  "Where resources went",
  "Who it worked with",
  "What happened",
  "What measurable impact was achieved",
];

// D. Transparency principles
const promises = [
  { icon: FileCheck, title: "Only real numbers", text: "If we can't document it, we don't publish it." },
  { icon: Camera, title: "Documented outcomes", text: "Counts, photos and notes from the ground." },
  { icon: Eye, title: "Shared openly", text: "Published here, linked to the work that created it." },
];

export default function Impact() {
  return (
    <main>
      <SEO
        title="Our Impact"
        description="PilCoo is at the beginning of its story. We won't publish numbers we can't stand behind — here's how impact will be measured and shared."
        path="/impact"
      />

      {/* STATEMENT */}
      <section className="pt-32 md:pt-44 pb-16 md:pb-20" data-testid="impact-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">Our impact</p>
          <h1 className="mt-6 font-serif tracking-tighter leading-[0.98] text-ink text-5xl sm:text-6xl lg:text-7xl max-w-4xl">
            <LineReveal lines={["Impact starts small —", "and it starts honestly."]} delay={0.15} />
          </h1>
          <Reveal delay={0.45}>
            <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-ink-2">
              We publish outcomes only when there's something real behind them.
              Every number here represents an action our community actually took,
              with more added as PilCoo's work grows.
            </p>
          </Reveal>
        </div>
      </section>

      {/* REAL NUMBER */}
      <section className="py-20 md:py-28 bg-ink text-sand" data-testid="impact-metrics">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight">What we measure</h2>
            <p className="mt-5 max-w-xl text-sm md:text-base leading-relaxed text-sand/60">
              Every metric below fills only when there's documented evidence
              behind it. Until then, it stays quietly empty.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-3 gap-px bg-sand/15 border border-sand/15 rounded-xl overflow-hidden">
            {metrics.map((metric, i) => (
              <Reveal key={metric.label} delay={i * 0.05} className={metric.live ? "col-span-2 bg-ink" : "bg-ink"}>
                <div
                  className={`p-7 md:p-9 h-full ${metric.live ? "border-l-4" : ""}`}
                  style={metric.live ? { borderColor: "#43D2FF" } : undefined}
                  data-testid={`impact-metric-${i}`}
                >
                  {metric.live ? (
                    <>
                      <p className="font-serif text-5xl md:text-7xl tracking-tight text-sand">{metric.value}</p>
                      <p className="mt-4 text-xs md:text-sm uppercase tracking-[0.15em] text-sand font-semibold">
                        {metric.label}
                      </p>
                      <p className="mt-2 text-xs md:text-sm text-sand/60">{metric.sub}</p>
                    </>
                  ) : (
                    <>
                      <p className="font-serif text-5xl md:text-6xl tracking-tight text-sand/30">—</p>
                      <p className="mt-4 text-xs md:text-sm uppercase tracking-[0.15em] text-sand/60">{metric.label}</p>
                      <p className="mt-2 text-xs text-sand/35">Not reported yet</p>
                    </>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REAL INITIATIVE */}
      <section className="py-20 md:py-28" data-testid="impact-story">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">Impact in practice</p>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="mt-8 border border-line rounded-xl bg-sand p-8 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <p className="flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.15em] text-ink-2">
                  <span className="h-2 w-2 rounded-full bg-pillar-animals" /> Community challenge
                </p>
                <h3 className="mt-4 font-serif text-3xl md:text-4xl tracking-tight text-ink">
                  1 Lakh Steps for Paws
                </h3>
                <p className="mt-4 text-sm md:text-base leading-relaxed text-ink-2 max-w-xl">
                  Our first community walking challenge set out to collectively
                  walk 1,00,000 steps for street-animal welfare. The community
                  crossed that goal — 1,20,827 steps — with 22 days still remaining.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3">
                <Link
                  to="/activities/1-lakh-steps-for-paws"
                  data-testid="impact-story-activity-link"
                  className="group rounded-full bg-action text-sand px-7 py-3.5 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-transform duration-300 hover:scale-[1.04] active:scale-95"
                >
                  See the Activity
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <a
                  href="https://www.mypacer.com/challenges/bxr5sj80/1-lakh-steps-for-kindness"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Join the Challenge on Pacer (opens in new tab)"
                  data-testid="impact-story-challenge-link"
                  className="rounded-full border border-ink px-7 py-3.5 text-sm font-semibold text-ink inline-flex items-center justify-center gap-2 transition-colors duration-300 hover:bg-ink hover:text-sand"
                >
                  Join the Challenge
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ANIMALS | PEOPLE | NATURE — expressive pillar tiles */}
      <section className="pb-20 md:pb-28" data-testid="impact-pillars">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">The three pillars</p>
            <h2 className="mt-4 font-serif text-3xl md:text-5xl tracking-tight text-ink max-w-3xl">
              One measure of honesty, <em className="text-ink-2">in three directions.</em>
            </h2>
            <p className="mt-5 max-w-2xl text-sm md:text-base leading-relaxed text-ink-2">
              We distinguish clearly between what PilCoo stands for, programmes
              being developed, active initiatives, and verified outcomes.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {impactPillars.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.08}>
                <div
                  className="h-full rounded-xl border p-7 md:p-8"
                  style={{ backgroundColor: `${p.color}14`, borderColor: `${p.color}4D` }}
                  data-testid={`impact-pillar-${p.name.toLowerCase()}`}
                >
                  <span
                    className="flex h-12 w-12 items-center justify-center rounded-full"
                    style={{ backgroundColor: p.color }}
                  >
                    <p.icon className="h-6 w-6" style={{ color: p.on }} aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 font-serif text-2xl md:text-3xl tracking-tight" style={{ color: p.text }}>
                    {p.name}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-2">{p.stand}</p>
                  <p
                    className="mt-6 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.12em]"
                    style={{ borderColor: `${p.color}80`, color: p.text, backgroundColor: `${p.color}1A` }}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${p.active ? "" : "opacity-50"}`}
                      style={{ backgroundColor: p.color }}
                    />
                    {p.status}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VISUAL REPORTING MODEL — Need → Action → Resources → Outcome → Evidence */}
      <section className="py-20 md:py-28 bg-sand-2 border-y border-line" data-testid="impact-formats">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">Our reporting format</p>
            <h2 className="mt-4 font-serif text-3xl md:text-5xl tracking-tight text-ink">
              How we'll report it
            </h2>
            <p className="mt-5 max-w-2xl text-sm md:text-base leading-relaxed text-ink-2">
              Every PilCoo outcome travels the same five stops before it is
              published — so you can always trace the number back to the work.
            </p>
          </Reveal>

          <div className="mt-12 flex flex-col lg:flex-row lg:items-stretch gap-4 lg:gap-3" data-testid="report-flow">
            {reportFlow.map((s, i) => (
              <Fragment key={s.label}>
                <Reveal delay={i * 0.07} className="flex-1">
                  <div
                    className="h-full flex items-center gap-4 rounded-xl border border-line bg-sand p-5 md:p-6"
                    data-testid={`report-step-${i + 1}`}
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ink text-sand">
                      <s.icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="font-serif text-lg md:text-xl tracking-tight text-ink">
                        <span className="mr-2 font-serif italic text-sm text-ink/30">{String(i + 1).padStart(2, "0")}</span>
                        {s.label}
                      </p>
                      <p className="mt-0.5 text-xs leading-relaxed text-ink-2">{s.note}</p>
                    </div>
                  </div>
                </Reveal>
                {i < reportFlow.length - 1 && (
                  <div className="flex items-center justify-center lg:w-4 shrink-0" aria-hidden="true">
                    <ArrowRight className="hidden lg:block h-4 w-4 text-ink/30" />
                    <ArrowDown className="lg:hidden h-4 w-4 text-ink/30" />
                  </div>
                )}
              </Fragment>
            ))}
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            {reportFormats.map((format, i) => (
              <Reveal key={format.title} delay={i * 0.08}>
                <div
                  className="h-full border border-dashed border-ink/25 rounded-xl p-6 md:p-7"
                  data-testid={`report-example-${i}`}
                >
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-ink-2">
                    Example — {format.title}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-ink-2">
                    {format.steps.join(" · ")} <span className="text-ink/40">— to be documented and published.</span>
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ACCOUNTABILITY STATEMENT */}
      <section className="py-24 md:py-32" data-testid="impact-transparency">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">Our commitment</p>
            <h2 className="mt-6 font-serif text-4xl md:text-6xl tracking-tight leading-[1.05] text-ink">
              Kindness deserves <em className="text-ink-2">accountability.</em>
            </h2>
            <p className="mt-7 max-w-2xl mx-auto text-base md:text-lg leading-relaxed text-ink-2">
              Open books, honest updates, and a clear line of sight from every
              rupee given to the care it becomes — no gloss, no spin. We run
              lean, keep administrative costs low, and treat every contribution
              as a trust, because that's exactly what it is.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-10 text-xs uppercase tracking-[0.2em] font-semibold text-ink-2">
              As funds, resources and effort flow, we will document:
            </p>
            <ul className="mt-5 flex flex-wrap justify-center gap-3" data-testid="accountability-items">
              {accountabilityItems.map((item) => (
                <li
                  key={item}
                  className="rounded-full border border-ink/20 bg-sand px-5 py-2.5 text-xs md:text-sm font-medium text-ink"
                >
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* THREE TRANSPARENCY PRINCIPLES + CTA */}
      <section className="pb-24 md:pb-32" data-testid="impact-promise">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {promises.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div
                  className="h-full flex items-start gap-5 rounded-xl border border-line bg-sand p-7"
                  data-testid={`promise-${i}`}
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-ink/25">
                    <p.icon className="h-5 w-5 text-ink" aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="font-serif text-xl md:text-2xl tracking-tight text-ink">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink-2">{p.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-16 text-center">
              <p className="font-serif text-2xl md:text-3xl tracking-tight text-ink max-w-xl mx-auto">
                The first number on this page could have your name behind it.
              </p>
              <Link
                to="/get-involved"
                data-testid="impact-cta"
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-action text-sand px-7 py-3.5 text-sm font-semibold transition-transform duration-300 hover:scale-[1.04] active:scale-95"
              >
                Help create the first outcome
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
